import { memo, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

import { useAuthStore, getDisplayName } from "@/store/useAuthStore";
import {
  segmentsToText,
  useChatTrailStore,
  type ChatTrailMessage,
} from "@/features/tutor/store/useChatTrailStore";
import { toInitials } from "@/features/tutor/utils/initials";
import MathText from "@/features/tutor/components/MathText";
import { useTypewriter } from "@/features/tutor/hooks/useTypewriter";
import TurnStatus from "@/features/tutor/components/TurnStatus";

const DRAG_MIN_H = 110;
const DRAG_MAX_H = 560;
const EXPANDED_ABOVE_H = 300;

const BOTTOM_SLACK_PX = 24;

const COMPACT_MAX_H = 260;
const EXPANDED_MAX_H = 520;
const COMPACT_MESSAGE_COUNT = 3;

const StudentMessage = memo(
  ({ message, initials }: { message: ChatTrailMessage; initials: string }) => (
    <div className="flex w-fit max-w-[85%] flex-row-reverse items-end gap-2 self-end">
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#02734A] text-xs font-extrabold text-white shadow-[0_0_0_2px_rgba(255,255,255,0.2)]"
      >
        {initials}
      </span>
      <p className="w-fit rounded-2xl rounded-br-[4px] border border-white/15 bg-[#02734A] px-3.5 py-[9px] text-lg leading-[1.4] text-white shadow-[0_5px_16px_rgba(0,0,0,0.16)]">
        <MathText segments={message.segments} />
      </p>
    </div>
  ),
);
StudentMessage.displayName = "StudentMessage";

const TutorMessage = memo(({ message }: { message: ChatTrailMessage }) => {
  // The reveal can only lag what the clock has released, so with audio on the
  // voice still sets the pace and this just fills in between word timestamps.
  const text = segmentsToText(message.segments);
  const shown = useTypewriter(text);

  if (message.pending && !text) {
    return (
      <p
        aria-live="polite"
        className="w-fit self-start px-0.5 py-[3px] text-lg italic leading-[1.45] text-white/60"
      >
        <TurnStatus label={message.status ?? ""} />
      </p>
    );
  }

  return (
    <p
      className={`w-fit max-w-[94%] self-start px-0.5 py-[3px] text-lg leading-[1.45] ${
        message.error ? "text-[#FFB4B4]" : "text-[#EEF3F5]"
      }`}
    >
      {message.error ?? (
        <MathText segments={message.segments} revealed={shown.length} />
      )}
    </p>
  );
});
TutorMessage.displayName = "TutorMessage";

const ConversationWindow = ({ onClose }: { onClose: () => void }) => {
  const messages = useChatTrailStore((s) => s.messages);
  const displayName = useAuthStore((s) => getDisplayName(s.user));
  const [expanded, setExpanded] = useState(false);
  const [draggedHeight, setDraggedHeight] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const initials = toInitials(displayName);
  const shown = expanded ? messages : messages.slice(-COMPACT_MESSAGE_COUNT);
  const height = draggedHeight ?? (expanded ? EXPANDED_MAX_H : COMPACT_MAX_H);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = height;

    const onMove = (move: PointerEvent) => {
      const next = Math.min(
        DRAG_MAX_H,
        Math.max(DRAG_MIN_H, startHeight + (startY - move.clientY)),
      );
      setDraggedHeight(next);
      setExpanded(next > EXPANDED_ABOVE_H);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // A cancelled pointer (scroll takeover, palm rejection) never fires
      // pointerup, so without this the drag listeners leak.
      window.removeEventListener("pointercancel", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const toggleExpanded = () => {
    setDraggedHeight(null);
    setExpanded((value) => !value);
  };

  const stuckToBottom = useRef(true);
  const settled = useRef(false);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Follow the reply as it streams, unless the student has scrolled up to
    // read something earlier — then leave their position alone.
    if (!stuckToBottom.current) return;

    // Native smooth scrolling eases the jump as lines land. Instant on the
    // first paint so opening the window does not animate from the top.
    // scrollTo is absent in jsdom, and instant on the first paint so opening
    // the window does not animate down from the top.
    if (typeof el.scrollTo === "function") {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: settled.current ? "smooth" : "auto",
      });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    settled.current = true;
  }, [messages, expanded, height]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stuckToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK_PX;
  };

  return (
    <section
      aria-label="Conversation"
      className="font-mulish w-full overflow-hidden rounded-2xl border border-white/25 bg-[#282A34]/50 px-3.5 pb-3.5 pt-2.5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_16px_42px_rgba(3,5,12,0.28)] backdrop-blur-[28px] backdrop-saturate-125"
    >
      <div className="flex items-center gap-2 pb-1.5">
        <button
          type="button"
          onClick={toggleExpanded}
          aria-label={
            expanded ? "Collapse conversation" : "Expand conversation"
          }
          aria-expanded={expanded}
          className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] border border-white/20 bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
        <div
          data-conversation-sizer
          aria-hidden="true"
          onPointerDown={startDrag}
          className="mx-auto flex h-6 flex-1 cursor-ns-resize touch-none items-center justify-center"
        >
          <span aria-hidden className="h-1 w-12 rounded bg-white/40" />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close conversation"
          className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] border border-white/20 bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <X size={16} />
        </button>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        data-testid="conversation-scroll"
        className="scrollbar-hidden -mx-1 flex min-h-[56px] flex-col gap-2.5 overflow-y-auto px-1"
        style={{
          maxHeight: height,
          transition: draggedHeight
            ? undefined
            : "max-height 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {shown.map((message) =>
          message.role === "student" ? (
            <StudentMessage
              key={message.id}
              message={message}
              initials={initials}
            />
          ) : (
            <TutorMessage key={message.id} message={message} />
          ),
        )}
      </div>
    </section>
  );
};

export default memo(ConversationWindow);
